// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import {
  currentUserQuery,
  rolesQuery,
  listUsersQuery,
  getInviteQuery,
  checkUserPermissionsQuery,
  getUserQuery,
  userTechnicalQuery,
  userTechnicalGraphQueryVariables,
  userTrainingsGraphQueryResponse,
  userTrainingsGraphQueryVariables,
  userTrainingsQuery,
  userTrainingsRow,
  dashboardOrgUserEventsQuery,
  UserTechnicalResponse,
} from '../queries';
import {
  updateCurrentUserMutation,
  updateAvatarMutation,
  updateUserMutation,
  changeUserPasswordMutation,
  linkSocialIdentityMutation,
  unlinkSocialIdentityMutation,
} from '../mutations';
import {
  CurrentUserResult,
  UpdateCurrentUserResult,
  UpdateCurrentUserPayload,
  UpdateUserPayload,
  RolesResult,
  UpdateUserResult,
  LinkSocialIdentityPayload,
  LinkSocialIdentityResult,
  UnLinkSocialIdentityResult,
  PermissionResult,
  getUserResult,
} from './results';
import {
  UpdateCurrentUserInput,
  UserPasswordInput,
  UpdateUserInput,
  FilterInput,
} from './inputs';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { User, Role, Permission, MutationResponse } from '@models';
import { DashboardOrgUserEvents, SocialAuthProviders } from '@config';
import { updateInviteMutation } from '../mutations/user/update-invitation';
import { deleteInviteMutation } from '../mutations/user/delete-invitation';
import { cloneDeep } from 'lodash';
import {
  userEventsGraphQueryVariables,
  userEventsRow,
  userEventsGraphQueryResponse,
  userEventsQuery,
} from '../queries/user/get-user-events';

@Injectable({
  providedIn: 'root',
})
export class UserServiceGql {
  constructor(private apollo: Apollo) {}

  getCurrentUser(withPermissions = true): Observable<User> {
    return this.apollo
      .query<CurrentUserResult>({
        query: currentUserQuery,
        fetchPolicy: 'cache-first',
        variables: {
          withPermissions,
          basic: !withPermissions,
        },
      })
      .pipe(
        catchError(error => {
          if (
            error &&
            error.graphQLErrors &&
            error.graphQLErrors[0] &&
            error.graphQLErrors[0].extensions &&
            error.graphQLErrors[0].extensions.code === 'UNAUTHENTICATED'
          ) {
            return throwError(new Error('UNAUTHENTICATED'));
          }

          return of(null);
        }),
        map((res: any) => {
          if (!res) {
            return null;
          }

          return res.data.currentUser;
        })
      );
  }

  updateUserAvatar(file: File): Observable<any> {
    return this.apollo
      .mutate<any>({
        mutation: updateAvatarMutation,
        variables: { file },
        context: { useMultipart: true, hasUpload: true },
      })
      .pipe(
        map(res => res.data.updateUserAvatar),
        map(cloneDeep)
      );
  }

  updateCurrentUser(
    userInfo: UpdateCurrentUserInput
  ): Observable<UpdateCurrentUserPayload> {
    return this.apollo
      .mutate<UpdateCurrentUserResult>({
        mutation: updateCurrentUserMutation,
        variables: {
          userInfo,
        },
      })
      .pipe(
        map(res => res.data.updateCurrentUser),
        map(cloneDeep)
      );
  }

  updateUser(userInfo: UpdateUserInput): Observable<UpdateUserPayload> {
    return this.apollo
      .mutate<UpdateUserResult>({
        mutation: updateUserMutation,
        variables: {
          userInfo,
        },
      })
      .pipe(
        map(res => res.data.updateUser),
        map(cloneDeep)
      );
  }

  changeUserPassword(
    userInfo: UserPasswordInput
  ): Observable<UpdateCurrentUserPayload> {
    return this.apollo
      .mutate<UpdateCurrentUserResult>({
        mutation: changeUserPasswordMutation,
        variables: {
          userInfo,
        },
      })
      .pipe(
        map(res => res.data.updateCurrentUser),
        map(cloneDeep)
      );
  }

  getUserRoles(roleInput: FilterInput): Observable<Role[]> {
    return this.apollo
      .watchQuery<RolesResult>({
        query: rolesQuery,
        variables: {
          roleInput,
        },
      })
      .valueChanges.pipe(
        map(res => res.data.roles),
        map(cloneDeep)
      );
  }

  getUser(userId: string, noCache = false): Observable<User> {
    return this.apollo
      .watchQuery<getUserResult>({
        query: getUserQuery,
        fetchPolicy: noCache ? 'no-cache' : 'cache-first',
        variables: {
          userId,
        },
      })
      .valueChanges.pipe(
        map(res => res.data.getUser),
        map(cloneDeep)
      );
  }

  getUserTechnical(
    args: userTechnicalGraphQueryVariables
  ): Observable<UserTechnicalResponse> {
    return this.apollo
      .watchQuery<any>({
        query: userTechnicalQuery,
        fetchPolicy: 'cache-first',
        variables: args,
      })
      .valueChanges.pipe(
        map(res => res.data.getUserTechnical),
        map(cloneDeep)
      );
  }

  getUserEvents(
    args: userEventsGraphQueryVariables
  ): Observable<userEventsRow[]> {
    return this.apollo
      .watchQuery<userEventsGraphQueryResponse, userEventsGraphQueryVariables>({
        query: userEventsQuery,
        fetchPolicy: 'cache-first',
        variables: args,
      })
      .valueChanges.pipe(
        map(res => res.data.getUserEvents),
        map(cloneDeep)
      );
  }

  getUserTrainings(
    args: userTrainingsGraphQueryVariables
  ): Observable<userTrainingsRow[]> {
    return this.apollo
      .watchQuery<
        userTrainingsGraphQueryResponse,
        userTrainingsGraphQueryVariables
      >({
        query: userTrainingsQuery,
        fetchPolicy: 'cache-first',
        variables: args,
      })
      .valueChanges.pipe(
        map(res => res.data.getUserTrainings),
        map(cloneDeep)
      );
  }

  listUsers() {
    return this.apollo
      .watchQuery<any>({
        query: listUsersQuery,
      })
      .valueChanges.pipe(
        map(res => res.data.listUsers),
        map(cloneDeep)
      );
  }

  linkSocialIdentity(
    userId: string,
    socialProvider: SocialAuthProviders
  ): Observable<LinkSocialIdentityPayload> {
    return this.apollo
      .mutate<LinkSocialIdentityResult>({
        mutation: linkSocialIdentityMutation,
        variables: {
          input: {
            userId,
            socialProvider,
          },
        },
      })
      .pipe(
        map(res => res.data.linkUserIdentity),
        map(cloneDeep)
      );
  }

  unlinkSocialIdentity(
    userId: string,
    socialProvider: SocialAuthProviders
  ): Observable<LinkSocialIdentityPayload> {
    return this.apollo
      .mutate<UnLinkSocialIdentityResult>({
        mutation: unlinkSocialIdentityMutation,
        variables: {
          input: {
            userId,
            socialProvider,
          },
        },
      })
      .pipe(
        map(res => res.data.unlinkUserIndentity),
        map(cloneDeep)
      );
  }

  getInvitation(inviteId: string): Observable<any> {
    return this.apollo
      .query<any>({
        query: getInviteQuery,
        variables: { inviteId },
      })
      .pipe(
        map(res => res.data.getInvite.data),
        map(cloneDeep)
      );
  }

  updateInvitation(inviteId: string, status: string): Observable<any> {
    return this.apollo
      .mutate<any>({
        mutation: updateInviteMutation,
        variables: {
          input: {
            inviteId,
            status,
          },
        },
      })
      .pipe(
        map(res => res.data),
        map(cloneDeep)
      );
  }

  deleteInvitation(inviteId: string): Observable<MutationResponse<null>> {
    return this.apollo
      .mutate<any>({
        mutation: deleteInviteMutation,
        variables: {
          input: {
            inviteId,
          },
        },
      })
      .pipe(
        map(res => res.data.deleteInvite),
        map(cloneDeep)
      );
  }

  checkUserPermissions(
    permissionRequest: string[],
    noCache = false
  ): Observable<Permission[]> {
    return this.apollo
      .query<PermissionResult>({
        query: checkUserPermissionsQuery,
        fetchPolicy: noCache ? 'no-cache' : 'cache-first',
        variables: { permissionRequest },
      })
      .pipe(
        map(res => res.data.checkUserPermissions),
        map(cloneDeep)
      );
  }

  getDashboardOrgUserEvents(
    organizationId,
    userId,
    noCache = false
  ): Observable<DashboardOrgUserEvents[]> {
    return this.apollo
      .watchQuery<any>({
        query: dashboardOrgUserEventsQuery,
        fetchPolicy: noCache ? 'no-cache' : 'cache-first',
        variables: {
          organizationId,
          userId,
        },
      })
      .valueChanges.pipe(
        map(res => res.data.dashboardOrgUserEvents),
        map(cloneDeep)
      );
  }
}
